import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { useConvexMutation } from '../use-convex-mutation';

export type { PlayerTrends } from '@/shared/types/evaluation.types';

export interface PlayerSummary {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  skillLevel: string | null;
  ballLevel: string | null;
  utrRating: number | null;
  photoUrl: string | null;
  attendance: number;
  lastEvaluation: { average: number } | null;
  coaches: { id: string; email: string }[];
}

export interface PlayerDetail {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  level: string | null;
  utr: string | null;
  createdAt: string;
  assignedCoaches: { id: string; email: string }[];
  parents: { id: string; email: string; relationshipType: string | null }[];
  stats: {
    attendance: number;
    totalSessions: number;
    totalEvaluations: number;
    evalAverages: {
      effort: number;
      engagement: number;
      competitionLevel: number;
      attitude: number;
    } | null;
  };
}

export interface CreatePlayerInput {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  level?: string;
  ballLevel?: string;
  utr?: string;
}

export interface UpdatePlayerInput extends Partial<CreatePlayerInput> {
  id: string;
}

type PlayerLevel = "beginner" | "intermediate" | "advanced" | "tournament";
type BallLevel = "red" | "orange" | "green" | "yellow";

export function useDirectorPlayers(search?: string, level?: string, ballLevel?: string) {
  const data = useQuery(api.director.players.list, { search, level, ballLevel });
  return { data, isLoading: data === undefined, error: null };
}

export function usePlayerDetail(playerId: string) {
  const data = useQuery(
    api.director.players.getDetail,
    playerId ? { playerId: playerId as Id<"players"> } : 'skip'
  );
  return { data, isLoading: data === undefined, error: null };
}

export function usePlayerTrends(playerId: string) {
  const data = useQuery(
    api.director.players.getTrends,
    playerId ? { playerId: playerId as Id<"players"> } : 'skip'
  );
  return { data, isLoading: data === undefined, error: null };
}

export function useCreatePlayer() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.players.create);
  return {
    isPending,
    mutateAsync: (args: CreatePlayerInput) => mutateAsync({
      firstName: args.firstName,
      lastName: args.lastName,
      dateOfBirth: args.dateOfBirth,
      level: args.level as PlayerLevel | undefined,
      ballLevel: args.ballLevel as BallLevel | undefined,
      utr: args.utr ? parseFloat(args.utr) : undefined,
    }),
  };
}

export function useUpdatePlayer() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.players.update);
  return {
    isPending,
    mutateAsync: (args: UpdatePlayerInput) => mutateAsync({
      playerId: args.id as Id<"players">,
      firstName: args.firstName,
      lastName: args.lastName,
      dateOfBirth: args.dateOfBirth,
      level: args.level as PlayerLevel | undefined,
      ballLevel: args.ballLevel as BallLevel | undefined,
      utr: args.utr ? parseFloat(args.utr) : undefined,
    }),
  };
}

export function useDeletePlayer() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.players.remove);
  return {
    isPending,
    mutateAsync: (playerId: string) => mutateAsync({ playerId: playerId as Id<"players"> }),
  };
}
